import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Edit3, X, Check, Trophy, Star } from "lucide-react";

const CATEGORIES = ["Klassiker", "Blockbuster", "Regie", "Zitate", "Soundtrack"];
const DIFFS = [
  { v: "leicht", l: "Leicht · 5 Pkt.",   c: "#8FBC8F" },
  { v: "mittel", l: "Mittel · 10 Pkt.",  c: "#D4AF37" },
  { v: "schwer", l: "Schwer · 20 Pkt.",  c: "#C0392B" },
];

export default function Trivia() {
  const [tab, setTab] = useState("questions"); // questions | leaderboard
  const [questions, setQuestions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [editing, setEditing] = useState(null); // question object or "new"
  const [busy, setBusy] = useState(false);

  const loadAll = () => {
    api.get("/admin/trivia/questions").then((r) => setQuestions(r.data)).catch(() => {});
    api.get("/admin/trivia/leaderboard").then((r) => setLeaderboard(r.data)).catch(() => {});
  };
  useEffect(loadAll, []);

  const del = async (q) => {
    if (!window.confirm(`Frage „${q.question.slice(0, 60)}…" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/trivia/questions/${q.id}`);
      toast.success("Gelöscht");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };

  const toggleActive = async (q) => {
    try {
      await api.patch(`/admin/trivia/questions/${q.id}`, { active: !q.active });
      loadAll();
    } catch (e) { toast.error("Fehler"); }
  };

  return (
    <div className="space-y-6" data-testid="trivia-admin-page">
      <div className="flex items-center gap-3">
        <Sparkles size={22} className="text-[#D4AF37]" />
        <h1 className="font-heading text-3xl">Film-Trivia · Fragenverwaltung</h1>
      </div>

      <div className="flex gap-1 border-b border-[#1A1A1A]">
        {[
          { v: "questions",   l: "Fragen",       icon: Star },
          { v: "leaderboard", l: "Leaderboard",  icon: Trophy },
        ].map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`trivia-tab-${t.v}`}
            className={`flex items-center gap-2 py-3 px-4 text-[10px] uppercase tracking-[0.28em] border-b-2 ${tab === t.v ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A8A398] hover:text-[#F5F0E6]"}`}>
            <t.icon size={13} /> {t.l}
          </button>
        ))}
      </div>

      {tab === "questions" && (
        <>
          <button onClick={() => setEditing("new")} data-testid="trivia-add-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black py-2 px-5 rounded text-[11px] uppercase tracking-widest">
            <Plus size={14} /> Neue Frage
          </button>

          <div className="space-y-2">
            {questions.map((q) => {
              const diff = DIFFS.find((d) => d.v === q.difficulty) || DIFFS[1];
              return (
                <div key={q.id} className="card-lux p-4 flex items-start gap-3" data-testid={`trivia-row-${q.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[9px] uppercase tracking-[0.32em] px-2 py-0.5 rounded-full border" style={{ borderColor: diff.c, color: diff.c }}>
                        {diff.l}
                      </span>
                      <span className="text-[9px] uppercase tracking-[0.32em] text-[#575148]">· {q.category}</span>
                      {!q.active && <span className="text-[9px] uppercase tracking-[0.32em] text-[#7A1921]">· INAKTIV</span>}
                    </div>
                    <div className="font-heading text-lg">{q.question}</div>
                    <div className="mt-1 text-xs text-[#A8A398] font-mono">
                      ✓ {q.options?.[q.correct_index] || "?"}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <button onClick={() => toggleActive(q)} data-testid={`trivia-toggle-${q.id}`}
                      className={`text-[9px] uppercase tracking-widest px-2 py-1 rounded border ${q.active ? "border-[#D4AF37]/40 text-[#D4AF37]" : "border-[#2A2A2A] text-[#575148]"}`}>
                      {q.active ? "aktiv" : "aus"}
                    </button>
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(q)} data-testid={`trivia-edit-${q.id}`}
                        className="p-1.5 border border-[#2A2A2A] rounded hover:border-[#D4AF37]"><Edit3 size={13} /></button>
                      <button onClick={() => del(q)} data-testid={`trivia-delete-${q.id}`}
                        className="p-1.5 border border-[#2A2A2A] rounded hover:border-[#7A1921] hover:text-[#EE9CA1]"><Trash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
            {!questions.length && <div className="text-center text-[#575148] py-10">Noch keine Fragen. Klick oben „Neue Frage"</div>}
          </div>
        </>
      )}

      {tab === "leaderboard" && <Leaderboard rows={leaderboard} />}

      {editing && <TriviaEditor q={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadAll(); }} />}
    </div>
  );
}

function Leaderboard({ rows }) {
  return (
    <div className="card-lux p-5" data-testid="trivia-leaderboard">
      <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mb-4 flex items-center gap-2">
        <Trophy size={14} /> Top-Filmkenner
      </div>
      {!rows.length ? (
        <div className="text-center text-[#575148] py-6">Noch keine Mitglieder haben Punkte gesammelt.</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.id} className="flex items-center gap-3 border border-[#1A1A1A] rounded p-3" data-testid={`leaderboard-row-${i}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading text-lg ${i < 3 ? "bg-[#D4AF37] text-black" : "border border-[#2A2A2A] text-[#A8A398]"}`}>
                {i + 1}
              </div>
              <div className="flex-1">
                <div className="text-sm">{r.first_name} {r.last_name}</div>
                <div className="text-[9px] uppercase tracking-widest text-[#575148]">Nr. {r.member_number} · {r.tier}</div>
              </div>
              <div className="font-mono text-lg text-[#D4AF37]">{r.trivia_points} <span className="text-[10px] uppercase tracking-widest text-[#A8A398]">Pkt.</span></div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function TriviaEditor({ q, onClose, onSaved }) {
  const [form, setForm] = useState(q || {
    question: "", options: ["", "", "", ""], correct_index: 0,
    explanation: "", category: "Klassiker", difficulty: "mittel", active: true,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const updOpt = (i, v) => { const opts = [...form.options]; opts[i] = v; setForm({ ...form, options: opts }); };

  const save = async () => {
    if (!form.question.trim()) return toast.error("Frage darf nicht leer sein");
    if (form.options.some((o) => !o.trim())) return toast.error("Alle 4 Antworten ausfüllen");
    setBusy(true);
    try {
      if (q?.id) await api.patch(`/admin/trivia/questions/${q.id}`, form);
      else await api.post("/admin/trivia/questions", form);
      toast.success("Gespeichert");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="trivia-editor">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-5">
          <h2 className="font-heading text-2xl text-[#D4AF37]">{q?.id ? "Frage bearbeiten" : "Neue Frage"}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Frage</label>
            <textarea rows={2} value={form.question} onChange={(e) => upd("question", e.target.value)} data-testid="trivia-editor-question"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
          </div>
          {form.options.map((opt, i) => (
            <div key={i} className="flex gap-2 items-center">
              <button type="button" onClick={() => upd("correct_index", i)} data-testid={`trivia-editor-correct-${i}`}
                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${form.correct_index === i ? "border-[#D4AF37] bg-[#D4AF37] text-black" : "border-[#2A2A2A] text-[#575148]"}`}>
                {form.correct_index === i ? <Check size={14} /> : String.fromCharCode(65 + i)}
              </button>
              <input value={opt} onChange={(e) => updOpt(i, e.target.value)} data-testid={`trivia-editor-option-${i}`}
                placeholder={`Antwort ${String.fromCharCode(65 + i)}${form.correct_index === i ? " (richtig)" : ""}`}
                className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Kategorie</label>
              <select value={form.category} onChange={(e) => upd("category", e.target.value)} data-testid="trivia-editor-category"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Schwierigkeit</label>
              <select value={form.difficulty} onChange={(e) => upd("difficulty", e.target.value)} data-testid="trivia-editor-difficulty"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2">
                {DIFFS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Erklärung (nach der Antwort angezeigt)</label>
            <textarea rows={2} value={form.explanation} onChange={(e) => upd("explanation", e.target.value)} data-testid="trivia-editor-explanation"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="py-2 px-4 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest">Abbrechen</button>
          <button onClick={save} disabled={busy} data-testid="trivia-editor-save"
            className="py-2 px-6 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-[11px] uppercase tracking-widest disabled:opacity-50">
            {busy ? "…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
